import React, { useEffect, useState } from "react"
import { Box, FormControl, FormErrorMessage, Input, Text, FormLabel, Button, Heading } from "@chakra-ui/react"
import { useNewsletter } from "@app/hooks/useNewsletter"
import { useInput } from "@app/hooks/useInput"

import Turnstile from "react-turnstile"
import { useAnalytics } from "@app/hooks/useAnalytics"
import { useBraze } from "@app/hooks/useBraze"
import { useCore } from "@app/hooks/useCore"
import { useTurnstile } from "@app/hooks/useTurnstile"
import Link from "../Link"

import AccountFormError from "@app/components/Account/AccountFormError"
import { useFunctions } from "@app/hooks/useFunctions"

import type { Location } from "@root/types/global"

type Props = {
  success: boolean
  setSuccess: (value: boolean) => void
  headSize?: string
  location: Location
}

const emailRegex = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/

const SubscribeFormDetailed: React.FC<Props> = ({ success, setSuccess, headSize, location }) => {
  const { callFunction } = useFunctions()
  const { newsletter, handleClose } = useNewsletter(location)
  const {
    turnstileError,
    isDisabled,
    turnstile_key,
    widgetId,
    turnstileToken,
    setIsDisabled,
    setTurnstileError,
    setTurnstileToken,
    setWidgetId,
  } = useTurnstile()
  const { trackUserIdentity, trackSubscriptionSubmit } = useAnalytics()
  const { trackOnlineSignup, identify } = useBraze()
  const {
    helpers: { decodeShopifyId },
  } = useCore()
  const [formError, setFormError] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const { value: firstName, bind: bindFirstName } = useInput({})
  const { value: email, bind: bindEmail } = useInput({ trim: true })

  const error = formError || turnstileError
  const nameLabel = newsletter?.additionalNameLabel || "Name"
  const nameError = newsletter?.additionalNameError || "Enter your name."
  const emailError = newsletter?.additionalEmailError || "Enter your email."
  const emailLabel = newsletter?.additionalEmailLabel || "Email"
  const successText = newsletter?.additionalSuccess || "Thank you for subscribing!"
  const successText2 = newsletter?.additionalSuccess1 || ""
  const successButtonLabel = newsletter?.additionalSuccessButtonlabel || "Start Shopping"
  const termComditionTitle = newsletter?.TCtitle
  const termComditionLink = newsletter?.TClink

  useEffect(() => {
    if (turnstileError) {
      setIsDisabled(true)
      setIsLoading(false)
      if (widgetId) window?.turnstile?.reset(widgetId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnstileError])

  // Turnstile tokens are single-use; reset the widget after each submit so a
  // resubmit (e.g. after an "email already taken" error) gets a fresh token
  // instead of reusing a consumed one (which Cloudflare rejects as timeout-or-duplicate).
  const resetTurnstile = () => {
    setTurnstileToken(null)
    setIsDisabled(true)
    if (widgetId) window?.turnstile?.reset(widgetId)
  }

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    setIsLoading(true)

    if (!email || (email && !emailRegex.test(email))) {
      setFormError(`An email address must be provided.`)
    } else {
      try {
        const result = await callFunction("customer-subscribe", {
          email,
          firstName,
          source: "popup",
          "cf-turnstile-response": turnstileToken,
        })

        if (result.status === "error") {
          // See SubscribeForm: never surface the raw Admin API message to shoppers, but log it.
          console.error("[SubscribeFormDetailed] subscribe failed", result)
          setFormError("Something went wrong. Please try again.")
          resetTurnstile()
          setIsLoading(false)
          return
        }

        const numericId = result.customer?.id ? decodeShopifyId(result.customer.id, "Customer") : null
        if (numericId) await identify(numericId, { logLoginEvent: false })
        await trackOnlineSignup({ source: "popup" })
        setSuccess(true)
        trackUserIdentity({ email })
        trackSubscriptionSubmit()
      } catch (e) {
        console.error(e)
        setFormError(`Something went wrong. Please try again.`)
        resetTurnstile()
      }
    }

    setIsLoading(false)
  }

  return (
    <Box
      as="form"
      onSubmit={onSubmit}
      w="full"
      mt={3}
      textAlign="left"
      sx={{
        "& label span": {
          ml: 0,
          color: "primary.midGrey",
        },
      }}
    >
      {!success ? (
        <>
          <FormControl mb={3} id="firstName" isRequired>
            <FormLabel fontSize="0.75rem" lineHeight={1.6667}>
              {nameLabel}
            </FormLabel>
            <Input variant="primary" placeholder={nameLabel} name="firstName" type="text" {...bindFirstName} />
            <FormErrorMessage>{nameError}</FormErrorMessage>
          </FormControl>

          <FormControl mb={3} id="email" isRequired>
            <FormLabel fontSize="0.75rem" lineHeight={1.6667}>
              {emailLabel}
            </FormLabel>
            <Input variant="primary" placeholder={emailLabel} name="email" type="email" {...bindEmail} />
            <FormErrorMessage>{emailError}</FormErrorMessage>
          </FormControl>

          <Text size="sm" m="auto" mt={2} mb={4}>
            {termComditionTitle}
            {termComditionLink?.pageLink && (
              <Link to={"/" + termComditionLink?.pageLink?.handle?.current} external={termComditionLink?.external}>
                {termComditionLink?.title}
              </Link>
            )}
          </Text>

          <Button
            isDisabled={isDisabled}
            isLoading={isLoading}
            variant="primary"
            size="small"
            type="submit"
            aria-label="Submit"
            mt={5}
            px={9}
            mx="auto"
            w="full"
          >
            Join the fun
          </Button>
          {turnstile_key ? (
            <Turnstile
              sitekey={turnstile_key}
              onVerify={token => {
                setTurnstileToken(token)
                setIsDisabled(false)
                setTurnstileError(null)
              }}
              onLoad={widgetId => {
                setWidgetId(widgetId)
                setIsDisabled(true)
              }}
              onExpire={() => {
                setIsDisabled(true)
                if (widgetId) window?.turnstile?.reset(widgetId)
              }}
              onError={error => {
                setIsDisabled(true)
                setTurnstileError(`Verification failed (${error}). Please refresh the page and try again.`)
              }}
            />
          ) : null}
          {error ? <AccountFormError message={error} /> : null}
        </>
      ) : (
        <>
          <Heading as="p" variant={headSize} p="15px 0" textAlign="center">
            {successText}
            <br />
            {successText2}
          </Heading>
          <Button variant="secondary" size="small" onClick={handleClose} mt={5} px={9} mx="auto" display="flex">
            {successButtonLabel}
          </Button>
        </>
      )}
    </Box>
  )
}

export default React.memo(SubscribeFormDetailed)
